import { useCallback, useEffect, useState } from 'react';
import { integrationService } from '@/services';
import type { IntercomOwnAppCredentials } from '@/services/intercom-integration.service';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';

const CONNECT_FAILURE_MESSAGE = 'Could not connect Intercom. Check the access token and client secret, then try again.';

interface UseIntercomOwnAppConnectOptions {
	refetchIntegrations: () => Promise<unknown>;
}

export interface UseIntercomOwnAppConnectResult {
	/** The webhook URL to register on the customer's app; null until loaded or when it failed to load. */
	webhookUrl: string | null;
	webhookUrlFailed: boolean;
	isConnecting: boolean;
	/** The reason the last connect failed, shown inline; null when nothing failed. */
	errorMessage: string | null;
	connect: (credentials: IntercomOwnAppCredentials) => Promise<void>;
}

/** Connecting through the customer's own Intercom app: load the webhook URL, then post the credentials. */
export const useIntercomOwnAppConnect = ({
	refetchIntegrations
}: UseIntercomOwnAppConnectOptions): UseIntercomOwnAppConnectResult => {
	const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
	const [webhookUrlFailed, setWebhookUrlFailed] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		integrationService
			.getIntercomOwnAppWebhookUrl()
			.then((url) => {
				if (isMounted) setWebhookUrl(url);
			})
			.catch((err: unknown) => {
				console.error('[useIntercomOwnAppConnect] webhook URL request failed', { err });
				if (isMounted) setWebhookUrlFailed(true);
			});
		return () => {
			isMounted = false;
		};
	}, []);

	const connect = useCallback(
		async (credentials: IntercomOwnAppCredentials) => {
			setIsConnecting(true);
			setErrorMessage(null);
			await integrationService
				.connectIntercomWithOwnApp(credentials)
				.then(async (workspaceId) => {
					toast.success('Intercom connected');
					await refetchIntegrations().catch((err: unknown) => {
						console.error('[useIntercomOwnAppConnect] refetch failed', { workspaceId, err });
					});
				})
				.catch((err: unknown) => {
					// The credentials are never logged: only the failure itself.
					console.error('[useIntercomOwnAppConnect.connect] connect request failed', { err });
					setErrorMessage(getApiErrorMessage(err, CONNECT_FAILURE_MESSAGE));
				});
			setIsConnecting(false);
		},
		[refetchIntegrations]
	);

	return { webhookUrl, webhookUrlFailed, isConnecting, errorMessage, connect };
};
